import React, { useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Eye, EyeOff, Flame } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useEarlyBird } from "@/hooks/useEarlyBird";

const PRIMARY = "#D95B38";

function SignupEarlyBirdNotice() {
  const { state } = useEarlyBird();
  if (!state || !state.active) return null;
  const monthly = state.pricing?.monthly;
  return (
    <div data-testid="signup-eb-notice" className="mb-6 border border-[#D95B38]/40 bg-[#221C19] p-4 flex items-start gap-3">
      <Flame className="w-4 h-4 text-[#D95B38] mt-0.5 shrink-0" strokeWidth={2} />
      <div className="font-mono text-xs leading-relaxed">
        <div className="text-[#F5F2EB]">
          You're claiming <span className="text-[#D95B38] font-semibold">founding pricing</span> — ${monthly?.current?.toFixed(2)}/mo locked in for life.
        </div>
        <div className="text-[#A39A8F] mt-1">
          {state.quota_remaining} of {state.quota} founding slots left. After that, new customers pay ${monthly?.regular?.toFixed(2)}/mo.
        </div>
      </div>
    </div>
  );
}

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
    <Navbar user={null} />
    <div className="mx-auto max-w-md px-6 py-16 md:py-24">{children}</div>
  </div>
);

const Heading = ({ kicker, title, sub }) => (
  <div className="mb-10 text-left">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">{kicker}</div>
    <h1 className="font-serif text-4xl md:text-5xl mb-3">{title}</h1>
    {sub && <p className="font-mono text-sm text-[#A39A8F] leading-relaxed">{sub}</p>}
  </div>
);

const Field = ({ label, children, hint }) => (
  <label className="block mb-5">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">{label}</span>
    {children}
    {hint && <span className="font-mono text-[10px] text-[#A39A8F] block mt-1.5">{hint}</span>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38] ${props.className || ""}`}
  />
);

const PasswordInput = (props) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={shown ? "text" : "password"}
        className={`w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 pr-10 focus:outline-none focus:border-[#D95B38] ${props.className || ""}`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        data-testid={`${props["data-testid"] || "password"}-toggle`}
        aria-label={shown ? "Hide password" : "Show password"}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
      >
        {shown ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
      </button>
    </div>
  );
};

const Submit = ({ children, loading, ...rest }) => (
  <button
    type="submit"
    disabled={loading}
    {...rest}
    className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-4 btn-press disabled:opacity-60 flex items-center justify-center gap-3"
  >
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
  </button>
);

const Divider = () => (
  <div className="flex items-center gap-3 my-7 text-[#A39A8F]">
    <div className="flex-1 border-t border-[#F5F2EB]/10" />
    <span className="font-mono text-[10px] uppercase tracking-[0.3em]">or</span>
    <div className="flex-1 border-t border-[#F5F2EB]/10" />
  </div>
);

const GoogleButton = ({ children = "Continue with Google" }) => {
  const startGoogleLogin = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      data-testid="auth-google-button"
      className="w-full font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-6 py-4 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors flex items-center justify-center gap-3"
    >
      <svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32zM12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23zM5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84zM12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      {children}
    </button>
  );
};

// ----------------- LOGIN -----------------
export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      nav("/dashboard");
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <Heading kicker="Welcome back" title="Sign in." sub="Pick up where you left off — your agents are waiting." />
      <form onSubmit={submit} data-testid="login-form">
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
        </Field>
        <Field label="Password">
          <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
        </Field>
        {error && <div className="font-mono text-xs text-[#D95B38] mb-4" data-testid="login-error">{error}</div>}
        <Submit loading={loading} data-testid="login-submit">{loading ? "Signing in…" : "Sign in →"}</Submit>
      </form>
      <div className="flex justify-between mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
        <Link to="/forgot-password" className="hover:text-[#F5F2EB]" data-testid="forgot-password-link">Forgot password?</Link>
        <Link to="/signup" className="hover:text-[#F5F2EB]" data-testid="signup-link">New here? Sign up →</Link>
      </div>
      <Divider />
      <GoogleButton />
    </Shell>
  );
}

// ----------------- SIGNUP -----------------
export function SignupPage() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // Pull referral attribution from localStorage (set by Landing on `?ref=widget&u=XXX`)
      let ref = null, ref_user = null;
      try {
        const stored = JSON.parse(localStorage.getItem("agentreply_ref") || "{}");
        if (stored && stored.user_id) {
          ref = stored.source || "widget";
          ref_user = stored.user_id;
        }
      } catch { /* ignore */ }
      // Capture signup source from URL — set by the landing-page preset demo banner
      // (?from=demo&preset=restaurant-host). Powers funnel analytics in /api/admin/health.
      const params = new URLSearchParams(window.location.search);
      const signup_source = params.get("from") || null;
      const signup_preset = params.get("preset") || null;
      await api.post("/auth/signup", { name, email, password, ref, ref_user, signup_source, signup_preset });
      setSent(true);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <Shell>
        <Heading kicker="Almost there" title="Check your email." sub={`We just sent a verification link to ${email}. Click it and you're in.`} />
        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 mb-6" data-testid="signup-sent-card">
          <div className="font-mono text-xs text-[#A39A8F] mb-4">Didn't get it? Check spam, then:</div>
          <button
            type="button"
            data-testid="resend-verification"
            onClick={async () => {
              try { await api.post("/auth/resend-verification", { email }); }
              catch {} // always succeeds server-side
            }}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline"
          >Resend verification email →</button>
        </div>
        <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <Heading kicker="Start your 14-day trial" title="Create your account." sub="No credit card. Build a working agent in under five minutes." />
      <SignupEarlyBirdNotice />
      <form onSubmit={submit} data-testid="signup-form">
        <Field label="Your name">
          <Input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" autoComplete="name" />
        </Field>
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" autoComplete="email" />
        </Field>
        <Field label="Password" hint="Minimum 8 characters.">
          <PasswordInput required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" autoComplete="new-password" />
        </Field>
        {error && <div className="font-mono text-xs text-[#D95B38] mb-4" data-testid="signup-error">{error}</div>}
        <Submit loading={loading} data-testid="signup-submit">{loading ? "Creating account…" : "Create account →"}</Submit>
      </form>
      <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
        Already have an account? <Link to="/login" className="hover:text-[#F5F2EB] text-[#D95B38]">Sign in →</Link>
      </div>
      <Divider />
      <GoogleButton>Sign up with Google</GoogleButton>
    </Shell>
  );
}

// ----------------- VERIFY EMAIL -----------------
export function VerifyEmailPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  React.useEffect(() => {
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        setTimeout(() => nav("/dashboard"), 1500);
      } catch (err) {
        setStatus("error");
        setMessage(formatErr(err?.response?.data?.detail) || err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Shell>
      {status === "verifying" && (
        <div data-testid="verify-loading" className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-6" style={{ color: PRIMARY }} />
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Verifying your email…</div>
        </div>
      )}
      {status === "success" && (
        <div data-testid="verify-success">
          <Heading kicker="Email verified" title="You're in." sub="Taking you to your dashboard…" />
        </div>
      )}
      {status === "error" && (
        <div data-testid="verify-error">
          <Heading kicker="Couldn't verify" title="Link expired or invalid." sub={message} />
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Back to sign in →</Link>
        </div>
      )}
    </Shell>
  );
}

// ----------------- FORGOT PASSWORD -----------------
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setSent(true);
    setLoading(false);
  };

  if (sent) {
    return (
      <Shell>
        <Heading kicker="Sent" title="Check your inbox." sub={`If an account with ${email} exists, a reset link is on its way. The link expires in 1 hour.`} />
        <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <Heading kicker="Forgot password" title="Reset it." sub="Enter your email and we'll send a one-hour reset link." />
      <form onSubmit={submit} data-testid="forgot-form">
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" autoComplete="email" />
        </Field>
        <Submit loading={loading} data-testid="forgot-submit">{loading ? "Sending…" : "Send reset link →"}</Submit>
      </form>
      <Link to="/login" className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> Back to sign in
      </Link>
    </Shell>
  );
}

// ----------------- RESET PASSWORD -----------------
export function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      nav("/dashboard");
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <Heading kicker="New password" title="Choose wisely." sub="At least 8 characters. We'll sign you in after." />
      <form onSubmit={submit} data-testid="reset-form">
        <Field label="New password" hint="Minimum 8 characters.">
          <PasswordInput required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" autoComplete="new-password" />
        </Field>
        <Field label="Confirm password">
          <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" autoComplete="new-password" />
        </Field>
        {error && <div className="font-mono text-xs text-[#D95B38] mb-4" data-testid="reset-error">{error}</div>}
        <Submit loading={loading} data-testid="reset-submit">{loading ? "Setting password…" : "Set new password →"}</Submit>
      </form>
    </Shell>
  );
}
